// Home.tsx (v4) — STEP-OV-HOME-TILES: 6-tile grid landing screen.
// All data flows through a single /api/pwa/home-digest call (same-source rule).
//
// Tiles:
//   1. OPERATOR QUEUE — blocked + in-progress steps (anchor tile)
//   2. ROADMAP        — phase close distance from phase-progress-truth
//   3. WAVE           — current wave + CI state
//   4. HEALTH         — services health + 7-row AUTO_COS precondition panel (VLR-2274)
//   5. CLOSURE        — milestone certificate progress
//   6. COST           — today spend vs monthly allocation + cap events (real spend, DEC-244)
//
// STEP-OV-HOME-TILES changes:
//   - Removed SystemHealthHero (no longer rendered at bottom of screen)
//   - Added click-through to owning tab for each tile (window.__goTab)
//   - HEALTH tile shows danger indicator when criticalVlrs > 0 or AUTO_COS is RED

import { useCallback, useEffect, useState } from "react";
import type { HomeDigestResponse, HomeDigestAutoCosGate } from "../../api/types.js";
import { useTimeShift } from "../TimeShift.js";
import { ConnectivityBanner } from "../../components/ConnectivityBanner.js";
import { CIQueueDepthTile } from "../../components/CIQueueDepthTile.js";
// getDigest is imported from digest-queries for single-fetch polling
// (PRIN-SIGNAL-INTEGRITY: single truth source, eliminates double-fetch).
import { getDigest } from "../lib/digest-queries.js";

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

const AUTO_COS_PRECONDITION_LABELS: Record<keyof HomeDigestAutoCosGate["preconditions"], string> = {
  restore_proven: "Restore proven",
  tier_b_enforced: "Tier-B enforced",
  prod_validation_fail_closed: "Prod validation fail-closed",
  rollback_surface_present: "Rollback surface",
  release_branch_resync: "Release branch resync",
  snapshot_on_cut: "Snapshot on cut",
  unattended_auth: "Unattended auth",
};

function TileHeader({ label }: { label: string }) {
  return (
    <div
      style={{
        fontSize: 9,
        textTransform: "uppercase",
        letterSpacing: 0.7,
        color: "var(--text-dim)",
        marginBottom: 8,
        fontWeight: 600,
      }}
    >
      {label}
    </div>
  );
}

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: 7,
        height: 7,
        borderRadius: "50%",
        background: ok ? "var(--ok)" : "var(--danger)",
        marginRight: 5,
        flexShrink: 0,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Tile 1: BLOCKED STEPS
// Shows operator_queue.waiting from home-digest (blocked steps only).
// Activity tab shows the broader attention queue (minus VLRs) — counts differ
// because attention queue includes decisions, mentions, and alerts beyond
// blocked steps.
// Click-through → Activity tab.
// ---------------------------------------------------------------------------

function OperatorQueueTile({ data }: { data: HomeDigestResponse["operator_queue"] }) {
  const totalWaiting = data.waiting.length;
  const totalActive = data.active.length;

  return (
    <div
      className="card"
      style={{ gridArea: "queue", minHeight: 120, margin: 0, cursor: "pointer" }}
      onClick={() => window.__goTab?.("activity")}
    >
      <TileHeader label="Blocked Steps" />
      <div style={{ fontSize: 26, fontWeight: 700, color: totalWaiting > 0 ? "var(--warn)" : "var(--ok)" }}>
        {totalWaiting}
      </div>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
        awaiting operator · {totalActive} in-progress
      </div>
      {data.waiting.slice(0, 3).map((item) => (
        <div
          key={item.itemId}
          style={{
            fontSize: 10,
            color: "var(--text-dim)",
            marginTop: 6,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          ▸ {item.title}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tile 2: ROADMAP
// Click-through → Roadmap tab.
// ---------------------------------------------------------------------------

function RoadmapTile({ data }: { data: HomeDigestResponse["roadmap"] }) {
  return (
    <div
      className="card"
      style={{ gridArea: "roadmap", margin: 0, cursor: "pointer" }}
      onClick={() => window.__goTab?.("roadmap")}
    >
      <TileHeader label="Roadmap" />
      <div style={{ fontSize: 14, fontWeight: 600 }}>{data.phase_name}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: "var(--accent)", marginTop: 4 }}>
        {data.open_steps}
      </div>
      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
        open steps · {data.exit_gate_items} exit gates
      </div>
      <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
        {data.cohorts.active_build > 0 && (
          <span style={{ fontSize: 9, color: "var(--accent)", fontFamily: "var(--font-mono)" }}>
            {data.cohorts.active_build} active
          </span>
        )}
        {data.cohorts.verify_only > 0 && (
          <span style={{ fontSize: 9, color: "var(--ok)", fontFamily: "var(--font-mono)" }}>
            {data.cohorts.verify_only} verify
          </span>
        )}
        {data.cohorts.spec_only > 0 && (
          <span style={{ fontSize: 9, color: "var(--warn)", fontFamily: "var(--font-mono)" }}>
            {data.cohorts.spec_only} spec
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tile 3: WAVE
// Click-through → Dispatch tab (wave details live on Dispatch).
// ---------------------------------------------------------------------------

function WaveTile({ data }: { data: HomeDigestResponse["wave"] }) {
  const ciOk = data.ciState === "green" || data.ciState === "success";
  const sha = data.deploySHA !== "unknown" ? data.deploySHA.slice(0, 7) : "—";
  return (
    <div
      className="card"
      style={{ gridArea: "wave", margin: 0, cursor: "pointer" }}
      onClick={() => window.__goTab?.("dispatch")}
    >
      <TileHeader label="Wave" />
      {/* STEP-ADCOS-DURABLE-WAKE: surface current_wave ID so operators can see stale pointer */}
      {data.currentWaveId != null && (
        <div
          style={{
            fontSize: 10,
            color: "var(--text-dim)",
            fontFamily: "var(--font-mono)",
            marginBottom: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          data-testid="wave-current-id"
        >
          {data.currentWaveId}
        </div>
      )}
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          fontFamily: "var(--font-mono)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {data.waveState !== "unknown" ? data.waveState : "—"}
        {data.waveOpenStepCount != null && (
          <span
            style={{ fontSize: 10, fontWeight: 400, color: "var(--text-muted)", marginLeft: 6 }}
            data-testid="wave-open-step-count"
          >
            {data.waveOpenStepCount} open steps
          </span>
        )}
      </div>
      <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ fontSize: 11, display: "flex", alignItems: "center" }}>
          <StatusDot ok={ciOk} />
          <span style={{ color: ciOk ? "var(--ok)" : "var(--warn)" }}>CI {data.ciState}</span>
        </div>
        <div style={{ fontSize: 10, color: "var(--text-dim)", fontFamily: "var(--font-mono)" }}>
          deploy {sha}
        </div>
        <div style={{ fontSize: 10, color: "var(--text-dim)" }}>
          tracks{" "}
          <span style={{ fontFamily: "var(--font-mono)", color: "var(--text)" }}>
            {data.runningTracks}
          </span>
        </div>
        <div style={{ fontSize: 10, color: "var(--text-dim)" }}>
          merged 24h{" "}
          <span style={{ fontFamily: "var(--font-mono)", color: "var(--text)" }}>
            {data.mergedPRs24h}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tile 4: HEALTH (includes 7-row AUTO_COS precondition panel — VLR-2274)
// Click-through → opens SystemHealth sheet via window.__openSystemHealth().
// Shows danger indicator when criticalVlrs > 0 or AUTO_COS is RED.
// ---------------------------------------------------------------------------

const PRECONDITION_KEYS = [
  "restore_proven",
  "tier_b_enforced",
  "prod_validation_fail_closed",
  "rollback_surface_present",
  "release_branch_resync",
  "snapshot_on_cut",
  "unattended_auth",
] as const;

interface HealthTileProps {
  data: HomeDigestResponse["health"];
  /** criticalVlrs > 0 triggers the danger indicator even if AUTO_COS is GREEN. */
  criticalVlrs?: number;
}

function HealthTile({ data, criticalVlrs = 0 }: HealthTileProps) {
  const gate = data.auto_cos_gate;
  const [expanded, setExpanded] = useState(false);

  const isUnhealthy = criticalVlrs > 0 || (gate?.overall !== "GREEN");

  if (!gate) {
    return (
      <div
        className="card"
        style={{
          gridArea: "health",
          margin: 0,
          cursor: "pointer",
          ...(isUnhealthy ? { borderLeft: "3px solid var(--danger)" } : {}),
        }}
        onClick={() => window.__openSystemHealth?.()}
        data-testid="health-tile"
      >
        <TileHeader label="Health" />
        <div style={{ fontSize: 11, color: "var(--text-dim)" }}>Health data unavailable</div>
      </div>
    );
  }

  const metCount = PRECONDITION_KEYS.filter((k) => gate.preconditions[k].met).length;

  return (
    <div
      className="card"
      style={{
        gridArea: "health",
        margin: 0,
        cursor: "pointer",
        ...(isUnhealthy ? { borderLeft: "3px solid var(--danger)" } : {}),
      }}
      onClick={() => window.__openSystemHealth?.()}
      data-testid="health-tile"
    >
      <TileHeader label="Health" />
      <div
        style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: expanded ? 10 : 4, cursor: "pointer" }}
        onClick={(e: { stopPropagation: () => void }) => { e.stopPropagation(); setExpanded((v: boolean) => !v); }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: gate.overall === "GREEN" ? "var(--ok)" : "var(--danger)",
            fontFamily: "var(--font-mono)",
          }}
        >
          AUTO_COS {gate.overall}
        </span>
        <span style={{ fontSize: 10, color: data.services_health === "ok" ? "var(--ok)" : "var(--warn)", fontFamily: "var(--font-mono)" }}>
          svc: {data.services_health ?? "—"}
        </span>
        <span style={{ fontSize: 10, color: "var(--text-muted)", flex: 1 }}>
          {metCount}/7
        </span>
        <span style={{ fontSize: 9, color: "var(--text-dim)" }}>{expanded ? "▲" : "▼"}</span>
      </div>
      {/* 7-row precondition panel — VLR-2274 mandatory, collapsible */}
      {expanded && (
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {PRECONDITION_KEYS.map((key) => {
            const cond = gate.preconditions[key];
            return (
              <div
                key={key}
                style={{ display: "flex", alignItems: "flex-start", gap: 4, fontSize: 10 }}
              >
                <StatusDot ok={cond.met} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ color: cond.met ? "var(--text)" : "var(--text-dim)", fontWeight: cond.met ? 500 : 400 }}>
                    {AUTO_COS_PRECONDITION_LABELS[key]}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tile 5: CLOSURE
// Click-through → Roadmap tab (closure is on Roadmap).
// ---------------------------------------------------------------------------

function ClosureTile({ data }: { data: HomeDigestResponse["closure"] }) {
  const trajectories = data.milestone_trajectories.slice(0, 4);

  return (
    <div
      className="card"
      style={{ gridArea: "closure", margin: 0, cursor: "pointer" }}
      onClick={() => window.__goTab?.("roadmap")}
    >
      <TileHeader label="Closure" />
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>
        {data.burn_rate.daily_avg.toFixed(1)}/day · {data.burn_rate.weekly_avg.toFixed(1)}/wk
      </div>
      {trajectories.length === 0 ? (
        <div style={{ fontSize: 11, color: "var(--text-dim)" }}>No active milestones</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {trajectories.map((m) => (
            <div key={m.milestone_id}>
              <div
                style={{
                  fontSize: 10,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  marginBottom: 2,
                  color: m.completion_pct >= 80 ? "var(--ok)" : m.completion_pct >= 50 ? "var(--accent)" : "var(--text-muted)",
                }}
              >
                {m.title}
              </div>
              <div style={{ height: 3, borderRadius: 2, background: "var(--border-soft)", overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    width: `${m.completion_pct}%`,
                    background: m.completion_pct >= 80 ? "var(--ok)" : m.completion_pct >= 50 ? "var(--accent)" : "var(--warn)",
                    borderRadius: 2,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tile 6: COST
// Click-through → Dispatch tab (cost/dispatch metrics on Dispatch).
// ---------------------------------------------------------------------------

function CostTile({ data }: { data: HomeDigestResponse["cost"] }) {
  const hasAllocation = data.monthlyAllocation != null && data.monthlyAllocation > 0;
  const pct = hasAllocation
    ? Math.round((data.todayUsage / data.monthlyAllocation!) * 100)
    : null;
  const spendColor =
    pct == null
      ? "var(--text)"
      : pct >= 90
        ? "var(--danger)"
        : pct >= 70
          ? "var(--warn)"
          : "var(--ok)";

  return (
    <div
      className="card"
      style={{ gridArea: "cost", margin: 0, cursor: "pointer" }}
      onClick={() => window.__goTab?.("dispatch")}
    >
      <TileHeader label="Cost" />
      <div style={{ fontSize: 22, fontWeight: 700, color: spendColor }}>
        ${data.todayUsage.toFixed(2)}
      </div>
      <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 8 }}>
        today · {hasAllocation ? `$${data.monthlyAllocation!.toFixed(0)}/mo` : "no budget set"}
      </div>
      {data.capEvents24h > 0 && (
        <div style={{ fontSize: 10, color: "var(--danger)", fontFamily: "var(--font-mono)" }}>
          {data.capEvents24h} cap event{data.capEvents24h !== 1 ? "s" : ""} 24h
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main screen
// ---------------------------------------------------------------------------

export function HomeScreen() {
  const { isLive } = useTimeShift();
  const [digest, setDigest] = useState<HomeDigestResponse | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  // criticalVlrs: derived inline from getDigest() result (single-fetch).
  // Used only for the HEALTH tile danger indicator.
  const [criticalVlrs, setCriticalVlrs] = useState<number>(0);

  const load = useCallback(async () => {
    try {
      const d = await getDigest();
      setDigest(d);
      setLoadError(null);
      // criticalVlrs is not yet exposed in HomeDigestResponse.health — default to 0.
      // (TODO: map from API when field is added. See digest-queries.ts healthStatus TODO.)
      setCriticalVlrs(0);
    } catch (err) {
      setLoadError(err);
    }
  }, []);

  useEffect(() => {
    void load();
    if (!isLive) return;
    const interval = setInterval(() => void load(), 30_000);
    return () => clearInterval(interval);
  }, [load, isLive]);

  if (loadError && !digest) {
    return <ConnectivityBanner error={loadError} variant="block" />;
  }

  if (!digest) {
    return (
      <div
        style={{
          padding: "40px 20px",
          textAlign: "center",
          color: "var(--text-dim)",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
        }}
      >
        Loading…
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 90 }}>
      {loadError != null && <ConnectivityBanner error={loadError} variant="inline" />}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gridTemplateAreas: `
            "queue  roadmap"
            "wave   health"
            "closure cost"
            "ciqueue ciqueue"
          `,
          gap: 8,
          padding: "10px 10px 0",
        }}
      >
        <OperatorQueueTile data={digest.operator_queue} />
        <RoadmapTile data={digest.roadmap} />
        <WaveTile data={digest.wave} />
        <HealthTile data={digest.health} criticalVlrs={criticalVlrs} />
        <ClosureTile data={digest.closure} />
        <CostTile data={digest.cost} />
        <CIQueueDepthTile />
      </div>
    </div>
  );
}
